import { useGetFrontTipiDomandaIdCompetenze } from '@framework/api-client';
import {
  IWidgetComponentProps,
  SelectWidgetWrapper,
  getWidgetProps,
  useOnEngineMessage,
} from '@framework/engine';
import { SelectInput, useField } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { Spin, Typography, message as antMessage } from 'antd';
import { useEffect, useMemo } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { useFillableSubmissionContext } from '../../FillableSubmissionContext';
import { ICompetenceWidget } from './ICompetenceWidget';
import { competenceSelectWidgetLabelName } from './createCompetenceSelectFormSchema';

export interface ICompetenceWidgetComponentProps
  extends IWidgetComponentProps<ICompetenceWidget> {}

export function CompetenceWidgetComponent(
  props: ICompetenceWidgetComponentProps,
) {
  const { widget } = props;

  const widgetOptions = getWidgetProps(props);
  const { submission } = useFillableSubmissionContext();
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetFrontTipiDomandaIdCompetenze(
    submission?.idTipoDomanda ?? 'na',
    {
      query: {
        enabled: submission?.idTipoDomanda != null && !props.readonly,
        keepPreviousData: true,
      },
    },
  );

  const idField = useField(widgetOptions.name);
  const labelFieldName = useMemo(
    () => competenceSelectWidgetLabelName(widgetOptions.name),
    [widgetOptions.name],
  );

  const { field: labelField } = useController({ name: labelFieldName });
  const { setValue: setLabelFieldValue } = useField(labelFieldName);

  // Campo label precedentemente selezionato
  const [labelValue, idValue] = useWatch({
    name: [labelFieldName, widgetOptions.name],
  });

  // Costruisco le opzioni della select
  const options = useMemo(() => {
    if (!data || props.readonly) {
      // Ritorno comunque il valore selezionato.
      return [{ label: labelValue, value: idValue }];
    }

    return (
      data
        ?.map(competence => ({
          label: competence.denominazione,
          value: competence.idCompetenza!,
          vectorLayerCode: competence.codiceIdentificativoLayerVettoriale,
        }))
        // Ordino le opzioni per label
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  }, [data, props.readonly, labelValue, idValue]);

  /**
   * In caso di selezione automatica di una competenza, la impostiamo qui
   * nel widget.
   */
  useOnEngineMessage(message => {
    if (message.type === 'competencePicked') {
      const competenceOption = data?.find(
        c =>
          c.codiceIdentificativoLayerVettoriale ==
          message.payload.competenceCode,
      );
      if (!competenceOption) {
        console.log(`[Competenze] Non trovata la competenza con codice ${message.payload.competenceCode}`, { data }); // prettier-ignore
        return;
      }

      if (idField.getValue() === competenceOption.idCompetenza) {
        return;
      }

      console.log(`[Competenze] Selezionata la competenza ${competenceOption.denominazione} con codice ${competenceOption.codiceIdentificativoLayerVettoriale}`); // prettier-ignore
      antMessage.info(
        t(
          'competence-widget.info-message',
          `È stata selezionata automaticamente la Competenza {{competenceName}} in base alle geometrie indicate.`,
          { competenceName: competenceOption.denominazione },
        ),
      );

      idField.setValue(competenceOption.idCompetenza, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      labelField.onChange(competenceOption.denominazione);
      labelField.onBlur();
    }
  });

  // Sinchronizzo il campo label con il campo ID; specie in caso di Default Value
  useEffect(() => {
    if (!idValue || props.readonly) return;
    const matchedOption = options.find(o => o.value === idValue);

    // Aggiorno il campo label se non corrisponde a quello selezionato.
    // Questo capita quando si seleziona una competenza automaticamente
    // (valore di default) e il campo label non è ancora stato aggiornato.
    if (matchedOption?.label && matchedOption.label !== labelValue) {
      setLabelFieldValue(matchedOption.label, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [idValue, labelValue, options, props.readonly, setLabelFieldValue]);

  return (
    <SelectWidgetWrapper
      {...props}
      hidden={widget.layout?.hide && !props.preview}
    >
      <SelectInput
        readOnly={props.readonly}
        name={widgetOptions.name}
        placeholder={widgetOptions.placeholder}
        allowClear
        showSearch
        filterOption
        optionFilterProp="label"
        onChange={(value, option, onChange) => {
          onChange(value);
          labelField.onChange((option as any)?.label ?? null);
          labelField.onBlur();
        }}
        notFoundContent={
          isLoading ? (
            <Spin size="small" />
          ) : (
            t('competence-widget.no-element-found', 'Nessun elemento trovato.')
          )
        }
        loading={isLoading}
        options={options}
      />

      {error && !props.readonly && (
        <Typography.Text type="danger">
          {t(
            'competence-widget.error-fetching-competences',
            'Impossibile recuperare i dati delle Competenze. Riprovare più tardi.',
          )}
        </Typography.Text>
      )}
    </SelectWidgetWrapper>
  );
}
